import { lumi } from './lumi'

export interface OfflineSubmission {
  id: string
  surveyId: string
  surveyTitle?: string
  researcherId: string
  researcherName?: string
  language: string
  answers: Record<string, any>
  audioConsent?: boolean
  audioConsentAt?: string | null
  startedAt?: string
  completedAt: string
  isCancelled?: boolean
  cancellationReason?: string
  savedAt: string
  sendingAt?: number
  audioDataUrl?: string | null
  audioFileName?: string | null
  audioDurationSec?: number | null
}

const OFFLINE_QUEUE_KEY = 'dna_offline_submissions_queue'
const SURVEY_CACHE_PREFIX = 'dna_cached_survey:'
const PUBLIC_SURVEY_CACHE_PREFIX = 'dna_cached_public_survey:'
const RESEARCHER_ASSIGNMENTS_CACHE_PREFIX = 'dna_cached_assignments:'
export const DNA_AUDIO_BASE_DOMAIN = 'https://pesquisasdna.com.br'

/**
 * Converte qualquer URL de arquivo do Lumi para o domínio oficial pesquisasdna.com.br
 * e garante que o link mantenha a extensão e path correto.
 */
export const formatDnaAudioUrl = (rawUrl?: string | null, _fileName?: string | null): string => {
  if (!rawUrl) return ''
  let url = rawUrl.trim()

  if (/https?:\/\/[^/]*lumi\.new/i.test(url)) {
    url = url.replace(/https?:\/\/[^/]*lumi\.new/i, DNA_AUDIO_BASE_DOMAIN)
  }

  return url
}

const AUDIO_DB_NAME = 'dna_audio_db'
const AUDIO_STORE_NAME = 'audios'
const AUDIO_DB_VERSION = 1

interface StoredAudio {
  id: string
  responseId?: string
  url?: string
  fileName?: string
  blob: Blob
  mimeType: string
  createdAt: string
}

function openAudioDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB indisponível'))
      return
    }
    const request = window.indexedDB.open(AUDIO_DB_NAME, AUDIO_DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(AUDIO_STORE_NAME)) {
        const store = db.createObjectStore(AUDIO_STORE_NAME, { keyPath: 'id' })
        store.createIndex('responseId', 'responseId', { unique: false })
        store.createIndex('url', 'url', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

export async function saveAudioToIndexedDB(
  key: string,
  blob: Blob,
  fileName?: string,
  responseId?: string,
  url?: string
): Promise<void> {
  try {
    const db = await openAudioDb()
    const tx = db.transaction(AUDIO_STORE_NAME, 'readwrite')
    const store = tx.objectStore(AUDIO_STORE_NAME)
    const record: StoredAudio = {
      id: key,
      responseId,
      url,
      fileName,
      blob,
      mimeType: blob.type || 'audio/mpeg',
      createdAt: new Date().toISOString(),
    }
    store.put(record)
    if (responseId && responseId !== key) {
      store.put({ ...record, id: responseId })
    }
    if (url && url !== key) {
      store.put({ ...record, id: url })
    }
    await new Promise((res, rej) => {
      tx.oncomplete = () => res(true)
      tx.onerror = () => rej(tx.error)
    })
  } catch (err) {
    console.warn('Falha ao salvar áudio no IndexedDB:', err)
  }
}

export async function getAudioFromIndexedDB(key: string): Promise<Blob | null> {
  if (!key) return null
  try {
    const db = await openAudioDb()
    const tx = db.transaction(AUDIO_STORE_NAME, 'readonly')
    const store = tx.objectStore(AUDIO_STORE_NAME)
    const request = store.get(key)
    return await new Promise<Blob | null>((resolve) => {
      request.onsuccess = () => {
        const record = request.result as StoredAudio | undefined
        resolve(record?.blob || null)
      }
      request.onerror = () => resolve(null)
    })
  } catch {
    return null
  }
}

export interface DownloadAudioOptions {
  responseId?: string | null
  audioData?: string | null
  surveyTitle?: string | null
}

/**
 * Realiza o download direto do arquivo de áudio para o dispositivo (.mp3),
 * exatamente igual ao download de PDF e Excel (via Blob local).
 * Não substitui a tela da aplicação nem abre reprodutor preto do navegador.
 */
export const downloadAudioDirectly = async (
  source?: string | Blob | File | null,
  rawFileName?: string | null,
  options?: DownloadAudioOptions
): Promise<void> => {
  const safeBase = (
    rawFileName ||
    (options?.responseId ? `audio_${options.responseId.slice(-8)}` : 'audio_entrevista')
  ).trim()

  const safeFileName = safeBase.toLowerCase().endsWith('.mp3')
    ? safeBase
    : `${safeBase.replace(/\.[^/.]+$/, '')}.mp3`

  const triggerBlobDownload = (blob: Blob) => {
    const mp3Blob = blob.type === 'audio/mpeg' || blob.type === 'audio/mp3'
      ? blob
      : new Blob([blob], { type: 'audio/mpeg' })

    const objectUrl = window.URL.createObjectURL(mp3Blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.setAttribute('download', safeFileName)
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    window.setTimeout(() => {
      try {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(objectUrl)
      } catch { /* ignore */ }
    }, 3000)
  }

  // 1. Se já for Blob ou File (ex: recém-gravado ou offline em memória)
  if (source instanceof Blob) {
    triggerBlobDownload(source)
    return
  }

  // 2. Se houver áudio em base64 (data: URL) em options ou no source
  const candidateData = options?.audioData || (typeof source === 'string' && source.startsWith('data:') ? source : '')
  if (candidateData && candidateData.startsWith('data:')) {
    try {
      const commaIdx = candidateData.indexOf(',')
      const base64Data = commaIdx >= 0 ? candidateData.slice(commaIdx + 1) : candidateData
      const byteChars = atob(base64Data)
      const byteNumbers = new Uint8Array(byteChars.length)
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i)
      }
      const blob = new Blob([byteNumbers], { type: 'audio/mpeg' })
      triggerBlobDownload(blob)
      return
    } catch (err) {
      console.warn('Erro ao decodificar áudio base64:', err)
    }
  }

  const url = typeof source === 'string' ? source.trim() : ''

  // 3. Checa o cache local IndexedDB (por responseId, url ou fileName)
  if (options?.responseId) {
    const cachedBlob = await getAudioFromIndexedDB(options.responseId)
    if (cachedBlob) {
      triggerBlobDownload(cachedBlob)
      return
    }
  }
  if (url) {
    const cachedBlob = await getAudioFromIndexedDB(url)
    if (cachedBlob) {
      triggerBlobDownload(cachedBlob)
      return
    }
  }
  if (rawFileName) {
    const cachedBlob = await getAudioFromIndexedDB(rawFileName)
    if (cachedBlob) {
      triggerBlobDownload(cachedBlob)
      return
    }
  }

  // 4. Se for blob: URL local
  if (url.startsWith('blob:')) {
    try {
      const res = await fetch(url)
      const blob = await res.blob()
      triggerBlobDownload(blob)
      return
    } catch {
      // continua para fallbacks seguros
    }
  }

  if (!url) return

  // 5. Tenta buscar remoto com CORS para converter em Blob e baixar direto
  try {
    const response = await fetch(url, { mode: 'cors' })
    if (response.ok) {
      const blob = await response.blob()
      triggerBlobDownload(blob)
      return
    }
  } catch {
    // Servidor remoto sem cabeçalho CORS ou erro de rede
  }

  // 6. PROTEÇÃO CONTRA TELA PRETA:
  // Nunca navegar a própria aba do aplicativo (o que causava a tela preta com o player).
  // Abre o link em nova aba independente com segurança.
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer')
    if (!win) {
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      window.setTimeout(() => {
        try { document.body.removeChild(link) } catch { /* ignore */ }
      }, 1500)
    }
  } catch (openErr) {
    console.warn('Falha ao abrir áudio em nova aba:', openErr)
  }
}

export const cacheSurvey = (survey: any): void => {
  if (!survey || (!survey._id && !survey.id)) return
  const id = survey._id || survey.id
  try {
    window.localStorage.setItem(`${SURVEY_CACHE_PREFIX}${id}`, JSON.stringify(survey))
  } catch (err) {
    console.warn('Erro ao salvar pesquisa no cache local:', err)
  }
}

export const getCachedSurvey = (surveyId: string): any | null => {
  try {
    const raw = window.localStorage.getItem(`${SURVEY_CACHE_PREFIX}${surveyId}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const cachePublicSurvey = (token: string, survey: any): void => {
  if (!token || !survey) return
  try {
    window.localStorage.setItem(`${PUBLIC_SURVEY_CACHE_PREFIX}${token}`, JSON.stringify(survey))
  } catch (err) {
    console.warn('Erro ao salvar pesquisa publica no cache local:', err)
  }
}

export const getCachedPublicSurvey = (token: string): any | null => {
  try {
    const raw = window.localStorage.getItem(`${PUBLIC_SURVEY_CACHE_PREFIX}${token}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const cacheResearcherAssignments = (researcherId: string, assignments: any[]): void => {
  if (!researcherId) return
  try {
    window.localStorage.setItem(
      `${RESEARCHER_ASSIGNMENTS_CACHE_PREFIX}${researcherId}`,
      JSON.stringify(assignments)
    )
  } catch (err) {
    console.warn('Erro ao salvar atribuicoes no cache local:', err)
  }
}

export const getCachedResearcherAssignments = (researcherId: string): any[] | null => {
  try {
    const raw = window.localStorage.getItem(`${RESEARCHER_ASSIGNMENTS_CACHE_PREFIX}${researcherId}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const getOfflineQueue = (): OfflineSubmission[] => {
  try {
    const raw = window.localStorage.getItem(OFFLINE_QUEUE_KEY)
    return raw ? (JSON.parse(raw) as OfflineSubmission[]) : []
  } catch {
    return []
  }
}

export const getOfflineQueueCount = (filter?: { surveyId?: string; researcherId?: string }): number => {
  const queue = getOfflineQueue()
  if (!filter) return queue.length
  return queue.filter((item) => {
    if (filter.surveyId && item.surveyId !== filter.surveyId) return false
    if (filter.researcherId && item.researcherId !== filter.researcherId) return false
    return true
  }).length
}

export const addOfflineSubmission = (submission: OfflineSubmission): void => {
  const queue = getOfflineQueue()
  const filtered = queue.filter((item) => item.id !== submission.id)
  filtered.push(submission)
  try {
    window.localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(filtered))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dna_offline_queue_changed', { detail: { count: filtered.length } }))
    }
  } catch (err) {
    console.error('Erro ao salvar submissao offline no storage:', err)
  }
}

export const removeOfflineSubmission = (id: string): void => {
  const queue = getOfflineQueue()
  const updated = queue.filter((item) => item.id !== id)
  try {
    window.localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(updated))
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dna_offline_queue_changed', { detail: { count: updated.length } }))
    }
  } catch (err) {
    console.error('Erro ao atualizar fila offline:', err)
  }
}

export const clearOfflineQueue = (): void => {
  try {
    window.localStorage.removeItem(OFFLINE_QUEUE_KEY)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dna_offline_queue_changed', { detail: { count: 0 } }))
    }
  } catch (err) {
    console.error('Erro ao limpar fila offline:', err)
  }
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Falha ao converter Blob para base64.'))
      }
    }
    reader.onerror = () => reject(new Error('Erro no leitor de arquivo de audio.'))
    reader.readAsDataURL(blob)
  })
}

export const base64ToFile = (dataUrl: string, fileName: string): File => {
  const arr = dataUrl.split(',')
  const mime = arr[0]?.match(/:(.*?);/)?.[1] || 'audio/mp3'
  const bstr = atob(arr[1] || '')
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], fileName, { type: mime })
}

export const isNetworkError = (error: any): boolean => {
  if (!navigator.onLine) return true
  if (!error) return false
  const msg = String(error.message || error).toLowerCase()
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network') ||
    msg.includes('offline') ||
    msg.includes('timeout') ||
    msg.includes('connection') ||
    msg.includes('econnrefused')
  )
}

export interface SyncResult {
  total: number
  sent: number
  failed: number
  errors: Array<{ id: string; error: string }>
}

let offlineSyncInFlight = false
const OFFLINE_SENDING_TTL_MS = 120000

// Marca a submissao como "enviando" na fila (persistido): outras abas e
// sincronizacoes simultaneas pulam o item e nao criam resposta duplicada.
const markOfflineSubmissionSending = (id: string): void => {
  const queue = getOfflineQueue()
  const updated = queue.map((item) => (item.id === id ? { ...item, sendingAt: Date.now() } : item))
  try {
    window.localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(updated))
  } catch (err) {
    console.error('Erro ao marcar submissao offline como enviando:', err)
  }
}

// Remove copias de uma mesma submissao (mesma pesquisa + pesquisador +
// horario de envio + status), mantendo a primeira ocorrencia. Nada e
// apagado do banco: a deduplicacao e apenas na leitura/exibicao.
export const dedupeResponseRecords = <T extends Record<string, any>>(responses: T[]): T[] => {
  const seen = new Set<string>()
  const unique: T[] = []
  responses.forEach((response) => {
    const identity = [
      String(response?.surveyId ?? ''),
      String(response?.researcherId ?? ''),
      String(response?.submittedAt ?? response?.completedAt ?? response?.createdAt ?? ''),
      String(response?.status ?? ''),
    ].join('|')
    if (seen.has(identity)) return
    seen.add(identity)
    unique.push(response)
  })
  return unique
}

export const syncOfflineSubmissions = async (
  filter?: { surveyId?: string; researcherId?: string },
  onProgress?: (current: number, total: number) => void
): Promise<SyncResult> => {
  // Impede sincronizacoes simultaneas: sincronizar a mesma submissao duas
  // vezes cria respostas duplicadas no banco.
  if (offlineSyncInFlight) {
    return { total: 0, sent: 0, failed: 0, errors: [] }
  }
  offlineSyncInFlight = true
  try {
    return await syncOfflineSubmissionsInner(filter, onProgress)
  } finally {
    offlineSyncInFlight = false
  }
}

const syncOfflineSubmissionsInner = async (
  filter?: { surveyId?: string; researcherId?: string },
  onProgress?: (current: number, total: number) => void
): Promise<SyncResult> => {
  const all = getOfflineQueue()
  const now = Date.now()
  const toSync = all.filter((item) => {
    if (filter?.surveyId && item.surveyId !== filter.surveyId) return false
    if (filter?.researcherId && item.researcherId !== filter.researcherId) return false
    // Item "enviando" por outra aba/sincronizacao recente: pula para nao
    // criar resposta duplicada (a marca expira em 2 minutos).
    if (typeof item.sendingAt === 'number' && now - item.sendingAt < OFFLINE_SENDING_TTL_MS) return false
    return true
  })

  const result: SyncResult = {
    total: toSync.length,
    sent: 0,
    failed: 0,
    errors: [],
  }

  if (toSync.length === 0) return result

  for (let i = 0; i < toSync.length; i++) {
    const item = toSync[i]
    if (onProgress) onProgress(i + 1, toSync.length)

    try {
      let audioFileUrl: string | undefined = undefined
      let audioName: string | undefined = undefined

      if (item.audioDataUrl && item.audioFileName) {
        try {
          const file = base64ToFile(item.audioDataUrl, item.audioFileName)
          const uploadRes = await lumi.tools.file.upload([file])
          if (uploadRes?.[0]?.fileUrl) {
            audioFileUrl = uploadRes[0].fileUrl
            audioName = item.audioFileName
          }
        } catch (uploadErr) {
          console.warn('Erro ao fazer upload do audio offline:', uploadErr)
        }
      }

      markOfflineSubmissionSending(item.id)

      const createdResponse = await lumi.entities.responses.create({
        offlineKey: item.id,
        surveyId: item.surveyId,
        surveyTitle: item.surveyTitle || undefined,
        researcherId: item.researcherId,
        researcherName: item.researcherName || undefined,
        language: item.language,
        status: item.isCancelled ? 'cancelled' : 'completed',
        cancellationReason: item.cancellationReason || undefined,
        answers: item.answers,
        audio_url: audioFileUrl || undefined,
        audio_nome: audioName || undefined,
        audio_data: item.audioDataUrl || undefined,
        audio_consent: item.audioConsent ?? false,
        audio_consentAt: item.audioConsentAt || undefined,
        submittedAt: item.completedAt,
        startedAt: item.startedAt || item.savedAt,
        completedAt: item.completedAt,
        createdAt: item.savedAt,
        updatedAt: new Date().toISOString(),
      })

      if (createdResponse && (createdResponse as any)._id) {
        removeOfflineSubmission(item.id)
        result.sent++
      } else {
        throw new Error('Resposta nao retornou ID valido')
      }
    } catch (err: any) {
      result.failed++
      result.errors.push({ id: item.id, error: String(err?.message || err) })
      if (isNetworkError(err)) {
        break
      }
    }
  }

  return result
}